import React, { useLayoutEffect, useRef, useState } from "react";
import { Select } from "antd";
import type {
  BaseOptionType,
  DefaultOptionType,
  SelectProps as AntSelectProps,
} from "antd/es/select";
import {
  FmFormField,
  type FmFormFieldMetaProps,
  type FmSelectFieldDescriptor,
  type FmSelectValue,
} from "./FmForm";
import { shouldVirtualizeOptions } from "./FmForm.helpers";
import { FmOverlayPortalContext } from "./FmOverlayPortalContext";
import type { RouteTheme } from "@/shared/config/designTokens";
import { FmFormControlThemeProvider } from "@/shared/ui/FmFormControlThemeProvider";

const cx = (...parts: Array<string | false | null | undefined>): string =>
  parts.filter(Boolean).join(" ");

export interface FmSelectOption<ValueType = unknown> {
  label: React.ReactNode;
  value: ValueType;
  disabled?: boolean;
  title?: string;
}

export interface FmSelectProps<
  ValueType = unknown,
  OptionType extends BaseOptionType | DefaultOptionType = DefaultOptionType,
> extends FmFormFieldMetaProps {
  descriptor?: FmSelectFieldDescriptor<ValueType>;
  id?: string;
  value?: ValueType;
  defaultValue?: ValueType;
  onChange?: AntSelectProps<ValueType, OptionType>["onChange"];
  options?: OptionType[];
  placeholder?: React.ReactNode;
  size?: AntSelectProps<ValueType, OptionType>["size"];
  mode?: AntSelectProps<ValueType, OptionType>["mode"];
  allowClear?: boolean;
  disabled?: boolean;
  loading?: boolean;
  showSearch?: boolean;
  virtual?: boolean;
  filterOption?: AntSelectProps<ValueType, OptionType>["filterOption"];
  optionRender?: AntSelectProps<ValueType, OptionType>["optionRender"];
  onSelect?: AntSelectProps<ValueType, OptionType>["onSelect"];
  defaultActiveFirstOption?: boolean;
  suffixIcon?: React.ReactNode;
  popupMatchSelectWidth?: boolean;
  placement?: AntSelectProps<ValueType, OptionType>["placement"];
  listHeight?: number;
  optionFilterProp?: string;
  ariaLabel?: string;
  "aria-label"?: string;
  controlWidth?: "xs" | "sm" | "md" | "lg" | "fill";
  /** Use `borderless` on over-map chrome so the plate owns the only stroke. */
  variant?: AntSelectProps<ValueType, OptionType>["variant"];
}

const toDescriptor = <
  ValueType,
  OptionType extends BaseOptionType | DefaultOptionType,
>(
  props: FmSelectProps<ValueType, OptionType>,
): FmSelectFieldDescriptor<FmSelectValue> => ({
  kind: "select",
  name: props.name ?? "",
  label: String(props.label ?? ""),
  controlWidth: props.controlWidth,
  helper: props.helper ? String(props.helper) : undefined,
  error: props.error ? String(props.error) : undefined,
  required: props.required,
  unit: props.unit ? String(props.unit) : undefined,
  basisNote: props.basisNote ? String(props.basisNote) : undefined,
  disabledReason: props.disabledReason
    ? String(props.disabledReason)
    : undefined,
  lockedReason: props.lockedReason ? String(props.lockedReason) : undefined,
  validation: props.validation,
  intent: props.intent,
  density: props.density,
  layoutPreset: props.layoutPreset,
  disabled: props.disabled,
  id: props.id,
  placeholder:
    typeof props.placeholder === "string" ? props.placeholder : undefined,
  size: props.size,
  testId: props["data-testid"],
  value: props.value as FmSelectValue,
  defaultValue: props.defaultValue as FmSelectValue,
  onChange:
    props.onChange as FmSelectFieldDescriptor<FmSelectValue>["onChange"],
  options: (props.options ?? []).map((option) => ({
    label: String(option.label ?? option.value ?? ""),
    value: option.value as FmSelectValue,
    disabled: option.disabled,
    title: option.title,
  })),
  allowClear: props.allowClear,
  loading: props.loading,
  showSearch: props.showSearch,
  variant: props.variant,
});

export function FmSelect<
  ValueType = unknown,
  OptionType extends BaseOptionType | DefaultOptionType = DefaultOptionType,
>(props: FmSelectProps<ValueType, OptionType>): React.ReactElement {
  const {
    name,
    label,
    helper,
    error,
    required,
    unit,
    basisNote,
    disabledReason,
    lockedReason,
    validation,
    intent,
    density,
    layoutPreset,
    "data-testid": dataTestId,
    id,
    value,
    defaultValue,
    onChange,
    options,
    placeholder,
    size,
    mode,
    allowClear,
    disabled,
    loading,
    showSearch,
    virtual,
    filterOption,
    optionRender,
    onSelect,
    defaultActiveFirstOption,
    suffixIcon,
    popupMatchSelectWidth,
    placement,
    listHeight,
    optionFilterProp,
    ariaLabel,
    "aria-label": ariaLabelAttr,
    controlWidth,
    variant,
  } = props;
  void required;
  void validation;
  void intent;
  void density;
  void layoutPreset;
  const anchorRef = useRef<HTMLSpanElement>(null);
  const overlayPortalRef = React.useContext(FmOverlayPortalContext);
  const [theme, setTheme] = useState<RouteTheme>("light");
  const mergedClassNames = {
    popup: {
      root: "fm-select__popup",
    },
  };
  const getPopupContainer = React.useCallback(
    () => overlayPortalRef?.current ?? document.body,
    [overlayPortalRef],
  );
  const resolveAmbientTheme = React.useCallback((): RouteTheme => {
    const themed = anchorRef.current?.closest("[data-theme]"); // design-rescue-ok: FmSelect is the DS-owned AntD form-control theme bridge.
    return themed?.getAttribute("data-theme") === "dark" ? "dark" : "light"; // design-rescue-ok: mirrors ambient DS band theme into the form-control provider.
  }, []);

  useLayoutEffect(() => {
    const syncTheme = () => setTheme(resolveAmbientTheme());
    syncTheme();
    if (typeof MutationObserver === "undefined") return undefined;
    const observer = new MutationObserver(syncTheme);
    observer.observe(document.documentElement, {
      attributes: true,
      subtree: true,
      attributeFilter: ["data-theme"], // design-rescue-ok: observe DS-owned theme boundary changes for the AntD form-control bridge.
    });
    return () => observer.disconnect();
  }, [resolveAmbientTheme]);

  if (props.descriptor) {
    return (
      <FmFormField
        descriptor={
          props.descriptor as unknown as FmSelectFieldDescriptor<FmSelectValue>
        }
      />
    );
  }

  const control = (
    <span ref={anchorRef} className="fm-select__theme-anchor">
      <FmFormControlThemeProvider theme={theme}>
        <Select<ValueType, OptionType>
          id={id}
          value={value}
          defaultValue={defaultValue}
          onChange={onChange}
          options={options}
          placeholder={placeholder}
          size={size}
          mode={mode}
          allowClear={allowClear}
          disabled={disabled}
          loading={loading}
          showSearch={showSearch}
          virtual={virtual ?? shouldVirtualizeOptions(options)}
          filterOption={filterOption}
          optionRender={optionRender}
          onSelect={onSelect}
          defaultActiveFirstOption={defaultActiveFirstOption}
          suffixIcon={suffixIcon}
          popupMatchSelectWidth={popupMatchSelectWidth}
          placement={placement}
          listHeight={listHeight}
          optionFilterProp={optionFilterProp}
          aria-label={ariaLabel ?? ariaLabelAttr}
          data-testid={dataTestId}
          getPopupContainer={getPopupContainer}
          variant={variant}
          className={cx(
            "fm-select",
            controlWidth && `fm-select--width-${controlWidth}`,
          )}
          classNames={mergedClassNames}
        />
      </FmFormControlThemeProvider>
    </span>
  );

  if (
    name ||
    label ||
    helper ||
    error ||
    unit ||
    basisNote ||
    disabledReason ||
    lockedReason
  ) {
    return <FmFormField descriptor={toDescriptor(props)} />;
  }

  return control;
}

export default FmSelect;
